import React, { useMemo, useState, useEffect } from 'react';
import { Typography } from 'antd';
import getFormattedValue from 'store/redux/utils/getFormattedValue';
import getFormattedNumber from 'store/redux/utils/getFormattedNumber';
import {
  mdiCashMultiple,
  mdiCart,
  mdiAccountMultiplePlus,
  mdiBank,
  mdiCash,
  mdiCashRefund,
  mdiCartPlus,
  mdiCalculatorVariant,
  mdiFormatListBulleted,
  mdiPercent,
} from '@mdi/js';
import {
  ValueDotFormatter,
  RoiFormatter,
  RoasFormatter,
  LtvValueFormatter,
} from 'components/tables/NinjaTable/formatters';
import WidgetCard from 'components/widgets/components/WidgetCard';
import { RoiDefinition, RoasDefinition } from 'components/NameDefinition';
import ReportInfo from 'components/ReportInfo/ReportInfo';
import WidgetCharts from './charts/WidgetCharts';
import { useWidget } from 'components/Widget/useWidget';

const { Text } = Typography;

const IntegrationWidget = ({
  height,
  data,
  integration,
  loading,
  widget_key,
  totals,
  exctraAction,
}) => {
  const { chartConfig, filters } = useWidget();

  const [isChartLoading, setIsChartLoading] = useState(false);

  useEffect(() => {
    if (chartConfig?.load_chart) {
      setIsChartLoading(true);
      const timer = setTimeout(() => {
        setIsChartLoading(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [chartConfig?.load_chart]);

  const metrics = useMemo(() => {
    const metricsArray = [
      {
        key: 'ad_spent',
        label: 'Ad Spent',
        icon: mdiCash,
        definition: { name: 'Ad Spent', description: 'All Ads Spent with Store URLs' },
        value: (
          <>
            {getFormattedValue(data.ad_spent)}
            <ReportInfo type='ad_spent' info={{ ...filters, integration: integration }} />
          </>
        ),
        exctraAction: exctraAction,
      },
      {
        key: 'gross_income',
        icon: mdiCashMultiple,
        label: 'Gross Income',
        definition: { name: 'Gross Income', description: 'Gross Revenue from the sales' },
        value: (
          <>
            <ValueDotFormatter value={data.gross_income} />
            <ReportInfo type='gross_income' info={{ ...filters, integration: integration }} />
          </>
        ),
      },
      {
        key: 'deals_cogs',
        icon: mdiCash,
        label: 'COGS',
        definition: {
          name: 'COGS',
          description: (
            <div className='max-w-72'>
              SUM of Cost of Goods Sold (COGS) <br /> If you have Product Costs set up in data, we
              automatically fetch them.If not, the COGS will default to 0, and you can manually
              enter the values on our Deals page for each order.
            </div>
          ),
        },
        value: getFormattedValue(data.deals_cogs) || 0,
      },
      {
        key: 'gross_profit',
        icon: mdiBank,
        label: 'Gross Profit',
        definition: { name: 'Gross Profit', description: 'Gross Income - COGS' },
        value: (
          <>
            {getFormattedValue(data.gross_profit)}
            <ReportInfo
              type='gross_profit'
              info={{
                ...filters,
                integration: integration,
              }}
            />
          </>
        ),
      },
      {
        key: 'net_profit',
        icon: mdiBank,
        label: 'Net Profit',
        definition: { name: 'Net Profit', description: 'Gross Profit - Ad Spent' },
        value: (
          <>
            {getFormattedValue(data.net_profit)}
            <ReportInfo
              type='net_profit'
              info={{
                ...filters,
                integration: integration,
              }}
            />
          </>
        ),
      },
      {
        key: 'roas',
        existDefinition: <RoasDefinition showIcon />,
        label: 'ROAS',
        formatter: () => <RoasFormatter row={data} fontSize={18} />,
      },
      {
        key: 'roi',
        existDefinition: <RoiDefinition showIcon />,
        label: 'ROI',
        formatter: () => <RoiFormatter row={data} fontSize={18} />,
      },
      {
        key: 'cac',
        icon: mdiCash,
        label: 'CAC',
        definition: { name: 'CAC', description: 'Ad Spent / Number of New Customer Orders' },
        value: getFormattedValue(data.cac) || 0,
      },
      {
        key: 'refund_rate',
        icon: mdiCashRefund,
        definition: {
          name: 'Refund Rate',
          description: (
            <>
              Percentage of sold products or services that are returned and refunded. It measures
              how often customers request refunds.
              <br />
              <Text code className='text-white text-sm'>
                Refund Rate: (Returned Orders Count / Total Orders Count) x 100
              </Text>
            </>
          ),
        },
        label: 'Refund Rate',
        value: `${getFormattedNumber(data?.refund_rate)}%`,
      },
      {
        key: 'quantity',
        icon: mdiFormatListBulleted,
        label: 'Unit Sold',
        definition: {
          name: 'Unit Sold',
          description: 'Total number of products that have been sold.',
        },
        value: `${getFormattedNumber(data?.quantity)}`,
      },
      {
        key: 'ltv',
        icon: mdiFormatListBulleted,
        definition: {
          name: 'Lifetime Value',
          description: (
            <>
              Lifetime Value (LTV) is the total revenue a business expects to earn from a customer
              over the entire duration of their relationship
              <br />
              <Text code className='text-white text-sm'>
                LTV: Average Order Value x Purchase Frequency x Customer Lifespan
              </Text>
            </>
          ),
        },
        label: 'Lifetime Value',
        value: `${getFormattedValue(data?.ltv)}`,
        // content: <LtvValueFormatter data={data} />,
      },

      {
        key: 'orders',
        icon: mdiCart,
        definition: { name: 'Orders', description: 'Total Number of Orders' },
        label: 'Orders',
        value: data.orders || 0,
      },
      {
        key: 'avg_order_value',
        label: 'Avg. Order Value',
        icon: mdiCalculatorVariant,
        definition: {
          name: 'Avg. Order Value',
          description: 'Total Gross Income / Number of Orders',
        },
        value: getFormattedValue(data.avg_order_value) || 0,
      },
      {
        key: 'avg_profit_per_order',
        icon: mdiCartPlus,
        label: 'Avg. Profit Per Order',
        definition: {
          name: 'Avg. Profit Per Order',
          description: 'Gross Profit / Number of Orders',
        },
        value: getFormattedValue(data.avg_profit_per_order) || 0,
      },
      {
        key: 'new_customer_orders',
        icon: mdiAccountMultiplePlus,
        label: 'New Customer Orders',
        definition: {
          name: 'New Customer Orders',
          description:
            'The number of orders placed by newly registered customers within the selected timeframe.',
        },
        value: data.new_customer_orders || 0,
      },
      {
        key: 'new_customer_percentage',
        icon: mdiPercent,
        label: 'New Customer Percentage',
        definition: {
          name: 'New Customer Percentage',
          description:
            'The percentage of total orders placed by newly registered customers within the selected timeframe.',
        },
        value: `${getFormattedNumber(data?.new_customer_percentage)}%`,
      },
      {
        key: 'repeat_customer_orders',
        icon: mdiCash,
        label: 'Repeat Customer Orders',
        definition: {
          name: 'Repeat Customer Orders',
          description:
            'The number of orders placed by returning customers within the selected timeframe.',
        },
        value: `${getFormattedNumber(data?.repeat_customer_orders)}`,
      },
      {
        key: 'repeat_customer_percentage',
        icon: mdiCash,
        definition: {
          name: 'Repeat Customer Percentage',
          description: 'The percentage of customers who have made more than one purchase.',
        },
        label: 'Repeat Customer Percentage',
        value: `${getFormattedNumber(data?.repeat_customer_percentage)} %`,
      },
    ];

    if (chartConfig?.metrics && Object.keys(chartConfig.metrics).length > 0) {
      const orderedKeys = Object.keys(chartConfig.metrics).filter(
        (key) => chartConfig.metrics[key]?.show
      );

      const metricsMap = Object.fromEntries(metricsArray.map((m) => [m.key, m]));
      return orderedKeys.map((key) => metricsMap[key]).filter(Boolean);
    }

    return metricsArray;
  }, [data, integration, loading, filters, widget_key, chartConfig]);

  return (
    <div
      style={{
        height: height - 50,
      }}
      className='grid gap-2 m-auto items-center h-full w-full md:grid-cols-[repeat(auto-fit,minmax(270px,1fr))] grid-cols-1'
    >
      {metrics.map((metric, index, key) => (
        <>
          {chartConfig?.load_chart ? (
            <WidgetCharts
              data={data.data}
              index={metric.key}
              totals={totals}
              loading={loading || isChartLoading}
              {...metric}
            />
          ) : (
            <WidgetCard loading={loading || isChartLoading} key={index} {...metric} />
          )}
        </>
      ))}
    </div>
  );
};

export default IntegrationWidget;
